import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, Pencil } from 'lucide-react';
import axios from 'axios';
import { DocumentExportActions } from './DocumentExportActions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PurchaseBillDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [docRes, prodRes, unitRes, taxRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/purchase-bills/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders())
        ]);
        setDoc(docRes.data);
        setProducts(prodRes.data);
        setUnits(unitRes.data);
        setTaxes(taxRes.data);
      } catch (err) { setError('Failed to load purchase bill'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const getName = (list, id, field = 'name') => list.find(i => i.id === id)?.[field] || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error) return <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>;
  if (!doc) return null;

  const exportConfig = {
    title: 'Purchase Bill', number: doc.bill_number,
    metadata: [
      { label: 'Bill Number', value: doc.bill_number || '-' }, { label: 'Bill Date', value: formatDate(doc.bill_date) },
      { label: 'Vendor', value: doc.vendor_name || '-' }, { label: 'Company', value: doc.company_name || '-' },
      { label: 'State of Supply', value: doc.state_of_supply || '-' },
    ],
    columns: [
      { label: '#', value: (_, index) => index + 1, type: 'number' }, { label: 'Product', value: item => getName(products, item.product_id) },
      { label: 'Description', value: item => item.description || '-' }, { label: 'MRP', value: item => Number(item.mrp || 0), type: 'number' },
      { label: 'Qty', value: item => Number(item.quantity || 0), type: 'number' }, { label: 'Unit', value: item => getName(units, item.unit_id) },
      { label: 'Price/Unit', value: item => Number(item.price_per_unit || 0), type: 'number' }, { label: 'Disc %', value: item => Number(item.discount_percentage || 0), type: 'number' },
      { label: 'Disc Amt', value: item => Number(item.discount_amount || 0), type: 'number' }, { label: 'Tax', value: item => item.tax_id ? getName(taxes, item.tax_id) : '-' },
      { label: 'Tax Amt', value: item => Number(item.tax_amount || 0), type: 'number' }, { label: 'Amount', value: item => Number(item.total_amount || 0), type: 'number' },
    ],
    rows: doc.line_items || [],
    totals: [
      { label: 'Subtotal', value: `INR ${Number(doc.subtotal || 0).toFixed(2)}`, rawValue: Number(doc.subtotal || 0) },
      { label: 'Total', value: `INR ${Number(doc.total || 0).toFixed(2)}`, rawValue: Number(doc.total || 0) },
      { label: 'Debit Notes Applied', value: `INR ${Number((doc.debit_note_allocations || []).reduce((sum, item) => sum + Number(item.amount || 0), 0)).toFixed(2)}`, rawValue: Number((doc.debit_note_allocations || []).reduce((sum, item) => sum + Number(item.amount || 0), 0)) },
      { label: 'Paid', value: `INR ${Number(doc.paid_amount || 0).toFixed(2)}`, rawValue: Number(doc.paid_amount || 0) },
      { label: 'Balance', value: `INR ${Number(doc.balance || 0).toFixed(2)}`, rawValue: Number(doc.balance || 0) },
    ],
    shareLines: [`Vendor: ${doc.vendor_name || '-'}`, `Date: ${formatDate(doc.bill_date)}`, `Total: INR ${Number(doc.total || 0).toFixed(2)}`, `Balance: INR ${Number(doc.balance || 0).toFixed(2)}`],
  };

  return (
    <div data-testid="purchase-bill-detail-page">
      <div className="flex items-center gap-4 mb-6 print:hidden">
        <button onClick={() => navigate('/admin/purchase/bills')} data-testid="back-btn"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Purchase Bills
        </button>
        <button onClick={() => navigate(`/admin/purchase/bills/${id}/edit`)} data-testid="edit-bill-btn"
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded-sm text-sm font-semibold transition-colors">
          <Pencil className="h-4 w-4" /> Edit Bill
        </button>
      </div>
      <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight mb-4">Purchase Bill Details</h1>
      <div className="mb-8"><DocumentExportActions config={exportConfig} testIdPrefix="purchase-bill" /></div>

      {/* Header Info */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Bill Number</span><span className="text-sm font-semibold text-slate-900">{doc.bill_number || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Bill Date</span><span className="text-sm text-slate-900">{formatDate(doc.bill_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Vendor</span><span className="text-sm text-slate-900">{doc.vendor_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Company</span><span className="text-sm text-slate-900">{doc.company_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">State of Supply</span><span className="text-sm text-slate-900">{doc.state_of_supply || '-'}</span></div>
          {doc.description && <div className="col-span-2"><span className="block text-xs font-medium text-slate-500 uppercase">Description</span><span className="text-sm text-slate-900">{doc.description}</span></div>}
        </div>
      </div>

      {(doc.debit_note_allocations || []).length > 0 && (
        <div className="bg-white rounded-sm border border-amber-200 mb-6">
          <div className="px-6 py-4 border-b border-amber-200 bg-amber-50"><h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Debit Notes Applied</h2></div>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase"><th className="px-4 py-3 text-left">Debit Note</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-right">Amount</th></tr></thead>
            <tbody>{doc.debit_note_allocations.map((allocation, index) => (
              <tr key={allocation.allocation_id || index} className="border-b border-slate-100"><td className="px-4 py-3"><button onClick={() => navigate(`/admin/purchase/return/${allocation.note_id}`)} className="font-semibold text-blue-600 hover:underline">{allocation.note_number || allocation.note_id}</button></td><td className="px-4 py-3 text-slate-600">{formatDate(allocation.allocated_at || allocation.note_date)}</td><td className="px-4 py-3 text-right font-semibold">₹{Number(allocation.amount || 0).toLocaleString('en-IN')}</td></tr>
            ))}</tbody>
          </table></div>
        </div>
      )}

      {/* Line Items */}
      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-right">MRP</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-right">Price/Unit</th>
                <th className="px-4 py-3 text-right">Disc %</th>
                <th className="px-4 py-3 text-right">Disc Amt</th>
                <th className="px-4 py-3 text-left">Tax</th>
                <th className="px-4 py-3 text-right">Tax Amt</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{getName(products, li.product_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{li.description || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.mrp || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.quantity}</td>
                  <td className="px-4 py-3">{getName(units, li.unit_id)}</td>
                  <td className="px-4 py-3 text-right">{li.price_per_unit?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{li.discount_percentage}%</td>
                  <td className="px-4 py-3 text-right">{li.discount_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                  <td className="px-4 py-3 text-right">{li.tax_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-medium">{li.total_amount?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-sm border border-slate-200 p-6">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-medium">₹{(doc.subtotal || 0).toFixed(2)}</span></div>
          {doc.round_off && <div className="flex justify-between text-sm"><span className="text-slate-500">Round Off</span><span>{(doc.round_off_amount || 0) >= 0 ? '+' : ''}{(doc.round_off_amount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Total</span><span>₹{(doc.total || 0).toLocaleString()}</span></div>
          {(doc.debit_note_allocations || []).length > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Debit Notes Applied</span><span className="text-amber-700 font-medium">₹{doc.debit_note_allocations.reduce((sum, item) => sum + Number(item.amount || 0), 0).toLocaleString('en-IN')}</span></div>}
          <div className="flex justify-between text-sm"><span className="text-slate-500">Paid</span><span className="text-green-600 font-medium">₹{(doc.paid_amount || 0).toLocaleString()}</span></div>
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Balance</span><span className={(doc.balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}>₹{(doc.balance || 0).toLocaleString()}</span></div>
        </div>
      </div>
    </div>
  );
};
